/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import { OrgUnitLocation, ProductDeliveryOptions, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';

/**
 * State information related to handle Store Selector scenarios.
 */
export interface IStoreSelectorStateManager {
    /**
     * Returns whether or not the store selection dialog is open.
     */
    readonly isDialogOpen: boolean;

    /**
     * Contextual information related to store selection instance.
     */
    readonly context?: IStoreSelectionStateContext;

    /**
     * Property for identifying which location is in active state.
     */
    readonly selectedStoreLocationId?: string;

    /**
     * Legal-entity-aware identity for the selected store.
     */
    readonly selectedStore?: IStoreInfo;

    /**
     * Property for identifying which store is last shown.
     */
    readonly lastInlineShownStoreInfo?: IStoreInfo;

    /**
     * Property for identifying which store is users preferred store.
     */
    readonly preferredStore?: IStoreInfo;

    /**
     * Property for identifying if map api is loaded.
     */
    readonly isMapApiLoaded?: boolean;

    /**
     * Property for identifying view state of List and Map.
     */
    listMapViewState: IListMapViewState;

    /**
     * Property for identifying if map module is loaded.
     */
    readonly isMapModuleLoaded?: boolean;

    /**
     * Sets the module map module loaded variable.
     */
    setMapModuleLoaded(input: boolean): Promise<void>;

    /**
     * Does the geo location step, transforming the search string into a location.
     */
    geoLocate(searchTerm: string, actionContext: IActionContext): Promise<IStoreSelectorLocation | undefined>;

    /**
     * Add a script to body if not already added and makes sure that map api is ready.
     */
    loadMapApi(input: ILoadMapApiInput): Promise<void>;

    /**
     * Sets current location list to the context.
     */
    setOrgUnitStoreInformation(input: IStoreSelectionStateContext): Promise<void>;

    /**
     * Sets last inline store information.
     */
    setLastInlineStoreInfo(latitude?: number, longitude?: number): Promise<void>;

    /**
     * Provides clicked store selected state.
     */
    setSelectedStoreLocationId(storeLocationId: string | undefined, dataAreaId?: string, inventoryLocationId?: string): Promise<void>;

    /**
     * Sets preferredStoreLocation.
     */
    setPreferredStoreLocation(
        storeLocationId: string | undefined,
        storeName: string | undefined,
        writeToCookie: boolean,
        latitude?: number,
        longitude?: number,
        dataAreaId?: string,
        inventoryLocationId?: string
    ): Promise<void>;

    /**
     * Clears preferred store.
     */
    clearPreferredStore(writeToCookie: boolean): Promise<void>;

    /**
     * Toggels between the view state of List and Map.
     */
    toggleListMapViewState(): Promise<void>;

    /**
     * Opens the dialog with the provided context.
     */
    openDialog(input: IStoreSelectionStateContext): Promise<void>;

    /**
     * Close the dialog.
     */
    closeDialog(): Promise<void>;
}

export interface IStoreSelectionStateContext {
    /**
     * Optional product to use for inventory checks.
     */
    product?: SimpleProduct;

    /**
     * Id of the Store selector module.
     */
    id?: string;

    /**
     * Show the map view link in mobile view port.
     */
    showMapViewLink?: boolean;

    /**
     * Returns whether or not the store selection has dialog.
     */
    readonly isDialog?: boolean;

    /**
     * Optional location list.
     */
    orgUnitStoreInformation?: IFullOrgUnitAvailability[];

    /**
     * Optional location to use to specify which location is already selected.
     */
    alreadySelectedLocation?: OrgUnitLocation;

    /**
     * Product pickup mode from PDP page.
     */
    deliveryOptions?: ProductDeliveryOptions;

    /**
     * Property bag for other information that is needed without having to
     * modify the interfaces.
     */
    additionalProperties?: object;

    /**
     * Property for parent element.
     */

    parentElementRef?: React.RefObject<HTMLButtonElement>;

    /**
     * Callback for when a location is selected.
     */
    onLocationSelected(location: OrgUnitLocation, deliveryMode?: string): Promise<void>;
}

export interface IStoreSelectorLocation {
    latitude: number | undefined;
    longitude: number | undefined;
}

export interface IListMapViewState {
    displayList: boolean;
    displayMap: boolean;
}

export interface ILoadMapApiInput {
    key: string | undefined;
    lang: string | undefined;
    market: string | undefined;
    AzureMapEnabled?: boolean;
}

export interface IStoreInfo {
    StoreName?: string;
    StoreId?: string;
    Latitude?: number;
    Longitude?: number;
    DataAreaId?: string;
    InventoryLocationId?: string;
}

/**
 * Determines whether a location matches a persisted or selected store identity.
 * Identities created before Cross-LE continue to match by store ID.
 * @param location - Store location to compare.
 * @param storeInfo - Store identity to match.
 * @returns Whether the location represents the store identity.
 */
export const isStoreInfoLocation = (location: OrgUnitLocation | undefined, storeInfo: IStoreInfo | undefined): boolean => {
    if (!location || !storeInfo?.StoreId || location.OrgUnitNumber !== storeInfo.StoreId) {
        return false;
    }

    if (storeInfo.DataAreaId && location.DataAreaId !== storeInfo.DataAreaId) {
        return false;
    }

    if (storeInfo.InventoryLocationId && location.InventoryLocationId !== storeInfo.InventoryLocationId) {
        return false;
    }

    return true;
};
